import React from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import classNames from 'classnames'
import { Paragraph } from '@scentregroup/ui'
import ContentGroup from '../content-group'
import { CustomThemeContext } from '../../context'
import CallToActionWrapper from '../call-to-action-wrapper'
import Markdown from '../markdown/index'
import Module from './module'
import style from './index.scss'

const NOT_FOUND = -1

const Modules = ({
  className,
  modules,
  initialColor,
  initialAngle,
  useDeckBG,
  centreTitle,
  children = null,
}) => {
  let color = initialColor === 'White' ? 'Grey' : 'White'
  let angle = initialAngle === 'top-left' ? 'top-right' : 'top-left'
  return (
    <CustomThemeContext.Consumer>
      {themeOption => (
        <div className={classNames(className, style.moduleGroup)}>
          {modules.map((module, i) => {
            color = color === 'White' ? 'Grey' : 'White'
            angle = angle === 'top-left' ? 'top-right' : 'top-left'

            const moduleId = get(module, 'sys.id')
            const moduleType = get(module, 'sys.contentTypeId')
            const displayMode = get(module, 'displayMode')

            switch (moduleType) {
              case 'section': {
                angle = 'top-left'
                let headingColor = 'Seasonal'
                if (
                  module.displayMode === 'Section Heading' &&
                  themeOption.luxury
                ) {
                  headingColor = color
                }
                return (
                  <div key={`module-${i}`} className={style.moduleListSection}>
                    <Module
                      lazyLoad={i > 0}
                      moduleProps={{ ...module, color: headingColor }}
                    />
                  </div>
                )
              }

              case 'introductions':
                color = 'White'
                return (
                  <div key={`module-${i}`} className={style.moduleListFeature}>
                    <Module
                      lazyLoad={i > 0}
                      moduleProps={{ ...module, angleOrigin: angle }}
                    />
                  </div>
                )

              case 'utilityPanel': {
                return (
                  <div key={`module-${i}`} className={style.moduleListSection}>
                    <Module
                      lazyLoad={i > 0}
                      moduleProps={{
                        ...module,
                        angleOrigin: angle,
                        color,
                        origin: 'modules',
                      }}
                    />
                  </div>
                )
              }

              case 'basicContent': {
                return (
                  <div key={`module-${i}`}>
                    <Paragraph>{module.bodyCopy}</Paragraph>
                    {module.callsToAction.map(cta => (
                      <CallToActionWrapper
                        key={cta.id}
                        url={cta.address}
                        category={cta.category}
                        className={style.moduleCta}
                      />
                    ))}
                  </div>
                )
              }

              case 'contentBlock': {
                return (
                  <div key={`module-${i}`}>
                    {<Markdown content={module.bodyCopy} />}
                  </div>
                )
              }

              case 'dynamicContent':
                return <div key={`module-${i}`}>{children}</div>
              case 'promotion':
              case 'manualCollection':
              case 'automatedCollection':
              default:
                if (displayMode === 'Deck') {
                  if (!themeOption.luxury) {
                    color = 'Seasonal'
                  }
                }

                if (
                  ['Page Heading', 'Section Heading'].indexOf(displayMode) >
                  NOT_FOUND
                ) {
                  angle = 'top-left'
                  let headingColor = 'Seasonal'
                  if (displayMode === 'Section Heading' && themeOption.luxury) {
                    headingColor = color
                  }
                  return (
                    <div
                      key={`module-${i}`}
                      className={style.moduleListSection}
                    >
                      <Module
                        lazyLoad={i > 0}
                        moduleProps={{ ...module, color: headingColor }}
                      />
                    </div>
                  )
                }

                if (displayMode === 'Feature') {
                  color = 'White'
                  return (
                    <div
                      key={`module-${i}`}
                      className={style.moduleListFeature}
                    >
                      <Module
                        lazyLoad={i > 0}
                        moduleProps={{
                          ...module,
                          angleOrigin: angle,
                        }}
                      />
                    </div>
                  )
                }

                if (displayMode === 'Featured Content') {
                  color = 'Transparent'
                  angle = 'none'
                }

                return (
                  <ContentGroup
                    angleOrigin={angle}
                    color={color}
                    backgroundImage={
                      displayMode === 'Deck' && useDeckBG && themeOption.deckBG
                    }
                    key={`module-${moduleId || i}`}
                    // Need to pass a different key for when arbitary react Children are sent
                  >
                    <Module
                      lazyLoad={i > 0}
                      moduleProps={{
                        ...module,
                        angleOrigin: angle,
                        color,
                        centreTitle,
                      }}
                    />
                  </ContentGroup>
                )
            }
          })}
        </div>
      )}
    </CustomThemeContext.Consumer>
  )
}

Modules.propTypes = {
  modules: PropTypes.array,
  initialAngle: PropTypes.string,
  initialColor: PropTypes.string,
  className: PropTypes.string,
  useDeckBG: PropTypes.bool,
  centreTitle: PropTypes.string,
  children: PropTypes.element,
}

Modules.defaultProps = {
  initialColor: 'Grey',
  initialAngle: 'top-left',
  useDeckBG: false,
}

export default Modules
